/**
 * Mock data for products endpoints
 */

import { PaginatedResponse } from '@/lib/data-source/types';

// Import the Product interface from the service to ensure consistency
export interface Product {
  id: string;
  name: string;
  description: string;
  name_translations?: { [locale: string]: string };
  description_translations?: { [locale: string]: string };
  supported_languages?: string[];
  status: 'draft' | 'published' | 'archived' | 'suspended';
  version: string;
  endpoint_count: number;
  usage_stats?: {
    total_calls: number;
    last_24h: number;
    active_customers: number;
  };
  tags: string[];
  rate_limits?: {
    requests_per_minute?: number;
    requests_per_hour?: number;
    requests_per_day?: number;
  };
  authentication_type?: 'api_key' | 'oauth2' | 'jwt' | 'custom';
  
  // Sub-resources as nested data
  endpoints?: unknown[];
  automation_nodes?: unknown[];
  billing_config?: unknown;
  feature_flags?: unknown[];
  
  created_at: string;
  updated_at: string;
}

const mockProducts: Product[] = [
  {
    id: '1',
    name: 'Weather API',
    description: 'Real-time weather data and forecasts for any location worldwide',
    status: 'published',
    version: '2.0.0',
    endpoint_count: 5,
    usage_stats: {
      total_calls: 125430,
      last_24h: 1250,
      active_customers: 48
    },
    tags: ['weather', 'forecast', 'climate'],
    rate_limits: {
      requests_per_minute: 100,
      requests_per_hour: 1000,
      requests_per_day: 10000
    },
    authentication_type: 'api_key',
    created_at: '2024-01-15T10:30:00Z',
    updated_at: '2024-12-20T14:45:00Z',
  },
  {
    id: '2',
    name: 'Email Automation API',
    description: 'Send transactional and marketing emails with advanced tracking',
    status: 'published',
    version: '1.5.3',
    endpoint_count: 8,
    usage_stats: {
      total_calls: 89234,
      last_24h: 890,
      active_customers: 32
    },
    tags: ['email', 'automation', 'marketing'],
    rate_limits: {
      requests_per_minute: 60,
      requests_per_hour: 1000,
      requests_per_day: 5000
    },
    authentication_type: 'api_key',
    created_at: '2024-02-01T09:00:00Z',
    updated_at: '2024-12-18T11:20:00Z',
  },
  {
    id: '3',
    name: 'SMS Gateway',
    description: 'Global SMS delivery with real-time status tracking',
    status: 'published',
    version: '1.2.0',
    endpoint_count: 4,
    usage_stats: {
      total_calls: 45678,
      last_24h: 456,
      active_customers: 25
    },
    tags: ['sms', 'messaging', 'notifications'],
    rate_limits: {
      requests_per_minute: 30,
      requests_per_hour: 500,
      requests_per_day: 2000
    },
    authentication_type: 'api_key',
    created_at: '2024-03-10T14:00:00Z',
    updated_at: '2024-12-15T16:30:00Z',
  },
  {
    id: '4',
    name: 'Payment Processing API',
    description: 'Secure payment processing with multiple gateway support',
    status: 'draft',
    version: '0.9.0',
    endpoint_count: 12,
    usage_stats: {
      total_calls: 0,
      last_24h: 0,
      active_customers: 0
    },
    tags: ['payments', 'finance', 'transactions'],
    rate_limits: {
      requests_per_minute: 20,
      requests_per_hour: 200,
      requests_per_day: 1000
    },
    authentication_type: 'oauth2',
    created_at: '2024-11-01T10:00:00Z',
    updated_at: '2024-12-10T09:15:00Z',
  },
];

export const productsMockData = {
  '/products': (params?: { page?: number; per_page?: number; search?: string; status?: string }): PaginatedResponse<Product> => {
    let filtered = [...mockProducts];
    
    // Apply filters
    if (params?.search) {
      const search = params.search.toLowerCase();
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(search) || 
        p.description.toLowerCase().includes(search) ||
        p.tags.some(tag => tag.toLowerCase().includes(search))
      );
    }
    
    if (params?.status && params.status !== 'all') {
      filtered = filtered.filter(p => p.status === params.status);
    }
    
    // Pagination
    const page = params?.page || 1;
    const perPage = params?.per_page || 10;
    const start = (page - 1) * perPage;
    const end = start + perPage;
    const paginated = filtered.slice(start, end);
    
    return {
      data: paginated,
      meta: {
        current_page: page,
        from: start + 1,
        last_page: Math.ceil(filtered.length / perPage),
        per_page: perPage,
        to: Math.min(end, filtered.length),
        total: filtered.length,
      },
      links: {
        first: `/api/v1/products?page=1`,
        last: `/api/v1/products?page=${Math.ceil(filtered.length / perPage)}`,
        prev: page > 1 ? `/api/v1/products?page=${page - 1}` : null,
        next: page < Math.ceil(filtered.length / perPage) ? `/api/v1/products?page=${page + 1}` : null,
      },
    };
  },

  '/products/:id': (id: string): { data: Product } => {
    const product = mockProducts.find(p => p.id === id);
    if (!product) {
      throw {
        response: {
          status: 404,
          data: {
            message: 'Product not found',
          },
        },
      };
    }
    return { data: product };
  },

  '/products/create': (data: Partial<Product>): { data: Product } => {
    // Create product
    const newProduct: Product = {
      id: (mockProducts.length + 1).toString(),
      name: data.name || 'New Product',
      description: data.description || '',
      status: data.status || 'draft',
      version: data.version || '1.0.0',
      endpoint_count: 0,
      usage_stats: {
        total_calls: 0,
        last_24h: 0,
        active_customers: 0
      },
      tags: data.tags || [],
      rate_limits: {
        requests_per_minute: 60,
        requests_per_hour: 1000,
        requests_per_day: 5000
      },
      authentication_type: data.authentication_type || 'api_key',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    mockProducts.push(newProduct);
    return { data: newProduct };
  },

  '/products/:id/update': (id: string, data: Partial<Product>): { data: Product } => {
    const index = mockProducts.findIndex(p => p.id === id);
    if (index === -1) {
      throw {
        response: {
          status: 404,
          data: {
            message: 'Product not found',
          },
        },
      };
    }
    
    mockProducts[index] = {
      ...mockProducts[index],
      ...data,
      updated_at: new Date().toISOString(),
    };
    
    return { data: mockProducts[index] };
  },

  '/products/:id/delete': (id: string): { message: string } => {
    const index = mockProducts.findIndex(p => p.id === id);
    if (index === -1) {
      throw {
        response: {
          status: 404,
          data: {
            message: 'Product not found',
          },
        },
      };
    }
    
    mockProducts.splice(index, 1);
    return { message: 'Product deleted successfully' };
  },
};